import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

const TOKEN_KEY = 'rf3_access_token';

export default function LoginPage() {
  const { isAuthenticated, isLoading, login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get redirect URL from query params (for app-initiated login)
  const redirectUrl = searchParams.get('redirect');

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      if (redirectUrl) {
        // If there's a redirect URL, send user there with token
        const token = localStorage.getItem(TOKEN_KEY);
        if (token) {
          try {
            const url = new URL(redirectUrl);
            url.searchParams.set('token', token);
            window.location.href = url.toString();
            return;
          } catch (e) {
            // Invalid URL, fall through to default behavior
            console.warn('Invalid redirect URL:', redirectUrl);
          }
        }
      }
      // Default: go to Hub dashboard
      navigate('/');
    }
  }, [isAuthenticated, isLoading, navigate, redirectUrl]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md rounded-xl shadow-[0_8px_32px_rgba(0,0,0,0.20)] overflow-hidden">
        <div className="rf3-rule" />
        <CardHeader className="text-center pt-8">
          <img
            src="/brand/logo-navy.png"
            alt="RF3"
            className="mx-auto mb-5 h-9 w-auto"
          />
          <span className="rf3-eyebrow">App Suite</span>
          <CardTitle className="text-2xl text-primary mt-1">Welcome back</CardTitle>
          <CardDescription>
            {redirectUrl
              ? 'Sign in to continue to your application.'
              : 'Sign in with your RF3 Google Workspace account to continue.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={login} className="w-full" size="lg">
            <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Sign in with Google
          </Button>
          {redirectUrl && (
            <p className="mt-4 text-xs text-center text-muted-foreground">
              You'll be redirected back to the application after signing in.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
